import {
  firebaseLogin,
  firebaseRegister,
  firebaseResetPassword,
  signInWithGoogle,
  signInWithApple,
} from "./firebaseAuth";
import { instance } from "../api/axiosInstance";
import UAParser from "ua-parser-js";
const axiosInstance = instance;

const parser = new UAParser();
const userAgent = parser.getResult();
if (userAgent.device.vendor === undefined) {
  userAgent.device.vendor = "";
}
if (userAgent.device.model === undefined) {
  userAgent.device.model = "";
}

export function getUserId() {
  return (
    window.localStorage.getItem("userId") ||
    window.sessionStorage.getItem("userId")
  );
}

export async function getProfile(userId, params = {}) {
  if (!userId) userId = getUserId();
  let url;
  if (params.cityId && params.cityUser) {
    url = `/users/${userId}?cityUser=true&cityId=${params.cityId}`; // IFrameListing.jsx and ViewPost.jsx
  } else {
    url = `/users/${userId}`;
  }
  return axiosInstance.get(url);
}

export async function fetchUsers(params = {}) {
  return axiosInstance.get("/users", { params });
}

export async function updateProfile(newProfileObj) {
  return axiosInstance.patch(`/users/${getUserId()}`, newProfileObj);
}

export async function getUserListings(params = {}, userId) {
  if (!userId) userId = getUserId();
  return axiosInstance.get(`/users/${userId}/listings`, { params });
} // instead we use getMyListing now

export async function deleteAccount() {
  return axiosInstance.delete(`/users/${getUserId()}`);
}

export async function updatePassword(credentials) {
  return axiosInstance.post(`/users/resetPassword`, credentials);
}

// Since the new Firebase login function directly sets the Authorization header dynamically,
// you no longer need the manually defined headers object.
// New login function using Firebase authentication

export const login = async ({ email, password }) => {
  try {
    const idToken = await firebaseLogin(email, password);

    window.localStorage.setItem("accessToken", idToken);
    window.sessionStorage.setItem("accessToken", idToken);

    const response = await instance.get("/users/currentUser", {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    const userData = response.data?.data;
    if (userData) {
      window.localStorage.setItem("userId", userData.id);
      window.sessionStorage.setItem("userId", userData.id);
    }

    return response;
  } catch (error) {
    console.error("Login failed:", error);
    throw error;
  }
};

export const register = async ({ email, password }) => {
  try {
    const user = await firebaseRegister(email, password);
    return user;
  } catch (error) {
    console.error("Registration failed:", error);
    throw error;
  }
};

export const resetPassword = async (email) => {
  try {
    await firebaseResetPassword(email);
    console.log("Password reset request successful");
  } catch (error) {
    console.error("Password reset request failed:", error);
    throw error;
  }
};

export const googleLogin = async () => {
  try {
    const idToken = await signInWithGoogle();

    const response = await instance.get("/users/currentUser", {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    return response;
  } catch (error) {
    const errData = error?.response?.data;
    if (errData?.errorCode === 8100) {
      return { data: errData };
    }

    throw error;
  }
};

export const appleLogin = async () => {
  try {
    const idToken = await signInWithApple();
    window.localStorage.setItem("accessToken", idToken);
    window.sessionStorage.setItem("accessToken", idToken);

    const response = await instance.get("/users/currentUser", {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    return { data: response.data, isAppleLoginSuccess: true };
  } catch (error) {
    const errData = error?.response?.data;

    if (errData?.errorCode === 8100) {
      return { data: errData, isAppleLoginSuccess: true };
    }

    return { isAppleLoginSuccess: false, error };
  }
};

export const getCurrentUser = async () => {
  try {
    const idToken = window.localStorage.getItem("accessToken");
    const response = await instance.get("/users/currentUser", {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching current user:", error);
    throw error;
  }
};

export const completeUserProfile = async (userData) => {
  try {
    const token =
      window.localStorage.getItem("accessToken") ||
      window.sessionStorage.getItem("accessToken");

    if (!token) {
      throw new Error("No authentication token found. Please log in again.");
    }

    const payload = {
      ...userData,
      firebaseToken: token,
    };

    const response = await instance.post("/users/register", payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response;
  } catch (error) {
    console.error("[Profile] User profile completion failed:", error);
    throw error;
  }
};

export async function logout(credentials) {
  return axiosInstance.post(`users/${getUserId()}/logout`, credentials);
}

export async function uploadProfilePic(formData) {
  return axiosInstance.post(`/users/${getUserId()}/imageUpload`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
}

export async function deleteProfilePic() {
  return axiosInstance.delete(`/users/${getUserId()}/imageDelete`);
}

export async function fetchDeviceList(params) {
  return axiosInstance.post(`/users/${getUserId()}/loginDevices`, {
    refreshToken: params,
  });
}

export async function logoutOfAllDevices() {
  return axiosInstance.delete(`/users/${getUserId()}/loginDevices`);
}

export async function logoutOfOneDevice(id) {
  return axiosInstance.delete(`/users/${getUserId()}/loginDevices?id=${id}`);
}
